import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { queryKeys } from '@/lib/queryKeys';

export type InquirySourceTable = 'inquiries' | 'storefront_inquiries';

export type InquiryStatus = 'waiting' | 'won' | 'lost';

export type Inquiry = {
	id: string;
	seller_id: string;
	item_id: string | null;
	guest_email: string;
	guest_name: string | null;
	message: string;
	offer_cents: number | null;
	source: 'storefront' | 'item';
	read_at: string | null;
	handled_at: string | null;
	created_at: string;
	/** Pipeline status. Legacy storefront_inquiries rows always report 'waiting'. */
	status: InquiryStatus;
	/** Set when status='won'. Records the price the deal actually closed at. */
	won_amount_cents: number | null;
	/** Set when status='lost'. Free-text reason (e.g. "Sold elsewhere"). */
	lost_reason: string | null;
	/** Which table this row physically lives in — routes mutations. */
	_table: InquirySourceTable;
};

export const useInquiries = () => {
	const { user } = useAuth();
	return useQuery({
		queryKey: queryKeys.inquiries.byUser(user?.id),
		enabled: !!user?.id,
		queryFn: async (): Promise<Inquiry[]> => {
			const [inquiriesRes, storefrontRes, blocksRes] = await Promise.all([
				supabase.from('inquiries').select('*').order('created_at', { ascending: false }),
				supabase
					.from('storefront_inquiries')
					.select('id, seller_user_id, buyer_name, buyer_email, message, read_at, handled_at, created_at')
					.order('created_at', { ascending: false }),
				supabase.from('inquiry_blocks').select('email'),
			]);
			if (inquiriesRes.error) throw inquiriesRes.error;
			// storefront_inquiries may fail silently for older schemas; don't hard-fail the whole inbox.
			const storefrontRows = storefrontRes.error ? [] : storefrontRes.data || [];

			const blocked = new Set(
				(blocksRes.data || []).map((b: any) => String(b.email).toLowerCase()),
			);

			const primary: Inquiry[] = ((inquiriesRes.data || []) as any[]).map((i) => {
				const raw = i.status as string | null;
				// DB default is 'new' (untouched by seller). Treat 'new' as 'waiting'
				// so the pipeline UI has an active tab from the first render.
				const status: InquiryStatus =
					raw === 'won' || raw === 'lost' ? raw : 'waiting';
				return {
					...i,
					status,
					won_amount_cents: i.won_amount_cents ?? null,
					lost_reason: i.lost_reason ?? null,
					_table: 'inquiries' as const,
				};
			});


			const legacy: Inquiry[] = (storefrontRows as any[]).map((r) => ({
				id: r.id,
				seller_id: r.seller_user_id,
				item_id: null,
				guest_email: r.buyer_email,
				guest_name: r.buyer_name ?? null,
				message: r.message ?? '',
				offer_cents: null,
				source: 'storefront' as const,
				read_at: r.read_at ?? null,
				handled_at: r.handled_at ?? null,
				created_at: r.created_at,
				status: 'waiting' as const,
				won_amount_cents: null,
				lost_reason: null,
				_table: 'storefront_inquiries' as const,
			}));

			return [...primary, ...legacy]
				.filter((i) => !blocked.has((i.guest_email || '').toLowerCase()))
				.sort((a, b) => (a.created_at < b.created_at ? 1 : -1));
		},
	});
};

export const useInquiriesUnreadCount = () => {
	const { user } = useAuth();
	return useQuery({
		queryKey: queryKeys.inquiries.unreadByUser(user?.id),
		enabled: !!user?.id,
		queryFn: async (): Promise<number> => {
			const [a, b] = await Promise.all([
				supabase
					.from('inquiries')
					.select('id', { count: 'exact', head: true })
					.is('read_at', null),
				supabase
					.from('storefront_inquiries')
					.select('id', { count: 'exact', head: true })
					.is('read_at', null),
			]);
			if (a.error) throw a.error;
			return (a.count ?? 0) + (b.error ? 0 : b.count ?? 0);
		},
	});
};

type TableRef = { id: string; table: InquirySourceTable };

export const useMarkInquiryRead = () => {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (ref: string | TableRef) => {
			const { id, table } =
				typeof ref === 'string' ? { id: ref, table: 'inquiries' as const } : ref;
			const { error } = await supabase
				.from(table)
				.update({ read_at: new Date().toISOString() })
				.eq('id', id);
			if (error) throw error;
		},
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: queryKeys.inquiries.all });
			qc.invalidateQueries({ queryKey: queryKeys.inquiries.unread });
		},
	});
};

export const useToggleInquiryHandled = () => {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async ({
			id,
			handled,
			table = 'inquiries',
		}: { id: string; handled: boolean; table?: InquirySourceTable }) => {
			const { error } = await supabase
				.from(table)
				.update({ handled_at: handled ? new Date().toISOString() : null })
				.eq('id', id);
			if (error) throw error;
		},
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: queryKeys.inquiries.all });
		},
	});
};

export const useDeleteInquiry = () => {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (ref: string | TableRef) => {
			const { id, table } =
				typeof ref === 'string' ? { id: ref, table: 'inquiries' as const } : ref;
			const { error } = await supabase.from(table).delete().eq('id', id);
			if (error) throw error;
		},
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: queryKeys.inquiries.all });
			qc.invalidateQueries({ queryKey: queryKeys.inquiries.unread });
		},
	});
};

export const useBlockInquirySender = () => {
	const { user } = useAuth();
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async ({
			email,
			inquiryId,
			table = 'inquiries',
		}: { email: string; inquiryId?: string; table?: InquirySourceTable }) => {
			if (!user?.id) throw new Error('Not signed in');
			const { error } = await supabase
				.from('inquiry_blocks')
				.insert({ user_id: user.id, email: email.trim().toLowerCase() });
			if (error && !String(error.message).toLowerCase().includes('duplicate')) throw error;
			if (inquiryId) {
				await supabase.from(table).delete().eq('id', inquiryId);
			}
		},
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: queryKeys.inquiries.all });
			qc.invalidateQueries({ queryKey: queryKeys.inquiries.unread });
		},
	});
};

export const useReportInquiry = () => {
	const { user } = useAuth();
	return useMutation({
		mutationFn: async (inquiry: Inquiry) => {
			if (!user?.id) throw new Error('Not signed in');
			const { error } = await supabase.from('reports').insert({
				reporter_id: user.id,
				reported_item_id: inquiry.item_id,
				type: 'guest_spam',
				description: `Reported guest inquiry from ${inquiry.guest_email}${inquiry.guest_name ? ` (${inquiry.guest_name})` : ''}. Message: ${inquiry.message}`,
				status: 'pending',
			});
			if (error) throw error;
		},
	});
};

/**
 * Set the pipeline status of an inquiry.
 *
 * - `won` records `won_amount_cents` and stamps `handled_at` (deal is closed).
 * - `lost` records `lost_reason` and stamps `handled_at`.
 * - `waiting` reopens the inquiry and clears the closed-state fields.
 *
 * Only works on rows in the `inquiries` table. Legacy `storefront_inquiries`
 * rows silently no-op — they don't have status columns.
 */
export const useSetInquiryStatus = () => {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async ({
			id,
			status,
			wonAmountCents,
			lostReason,
			table = 'inquiries',
		}: {
			id: string;
			status: InquiryStatus;
			wonAmountCents?: number | null;
			lostReason?: string | null;
			table?: InquirySourceTable;
		}) => {
			if (table !== 'inquiries') return;
			const patch: {
				status: InquiryStatus;
				won_amount_cents: number | null;
				lost_reason: string | null;
				handled_at: string | null;
			} = {
				status,
				won_amount_cents: status === 'won' ? (wonAmountCents ?? null) : null,
				lost_reason: status === 'lost' ? (lostReason ?? null) : null,
				handled_at: status === 'waiting' ? null : new Date().toISOString(),
			};
			const { error } = await supabase.from('inquiries').update(patch).eq('id', id);
			if (error) throw error;
		},
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: queryKeys.inquiries.all });
			qc.invalidateQueries({ queryKey: queryKeys.inquiries.unread });
		},
	});
};

// ============================================================================
// Inquiry replies — native seller → guest reply thread (replaces mailto:)
// ============================================================================

export type InquiryReply = {
	id: string;
	inquiry_id: string;
	inquiry_table: InquirySourceTable;
	seller_id: string;
	body: string;
	attachments: string[] | null;
	email_sent_at: string | null;
	created_at: string;
};

export const useInquiryReplies = (inquiryId: string, table: InquirySourceTable) => {
	const { user } = useAuth();
	return useQuery({
		queryKey: queryKeys.inquiries.replies(inquiryId, table, user?.id),
		enabled: !!user?.id && !!inquiryId,
		queryFn: async (): Promise<InquiryReply[]> => {
			const { data, error } = await supabase
				.from('inquiry_replies')
				.select('*')
				.eq('inquiry_id', inquiryId)
				.eq('inquiry_table', table)
				.order('created_at', { ascending: true });
			if (error) throw error;
			return (data ?? []) as InquiryReply[];
		},
	});
};

export const useSendInquiryReply = () => {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async ({
			inquiryId,
			inquiryTable,
			body,
			attachmentPaths,
		}: {
			inquiryId: string;
			inquiryTable: InquirySourceTable;
			body: string;
			attachmentPaths?: string[];
		}) => {
			const { data, error } = await supabase.functions.invoke('send-inquiry-reply', {
				body: { inquiryId, inquiryTable, body, attachmentPaths: attachmentPaths ?? [] },
			});
			if (error) throw error;
			if ((data as any)?.error) throw new Error((data as any).error);
			return data as { success: boolean; replyId?: string; emailQueued?: boolean };
		},
		onSuccess: (_data, vars) => {
			qc.invalidateQueries({ queryKey: queryKeys.inquiries.repliesFor(vars.inquiryId, vars.inquiryTable) });
			qc.invalidateQueries({ queryKey: queryKeys.inquiries.all });
			qc.invalidateQueries({ queryKey: queryKeys.inquiries.unread });
		},
	});
};
